import { useUserPermissions } from "@dashboard/auth/hooks/useUserPermissions";
import { Combobox } from "@dashboard/components/Combobox";
import { DashboardModal } from "@dashboard/components/Modal";
import { hasPermissions } from "@dashboard/components/RequirePermissions";
import { DEFAULT_INITIAL_SEARCH_DATA } from "@dashboard/config";
import { PermissionEnum } from "@dashboard/graphql";
import { buttonMessages } from "@dashboard/intl";
import { getFullName } from "@dashboard/misc";
import useCustomerSearch from "@dashboard/searches/useCustomerSearch";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { Box, Button, Text } from "@saleor/macaw-ui-next";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import { prepareCheckoutMessages as messages } from "./messages";
import { QuotedFreightField } from "./QuotedFreightField";
import { PrepareCheckoutCustomer, usePrepareCheckout } from "./usePrepareCheckout";

/**
 * The dialog body lives in its own component so the customer search mounts with
 * the dialog. The order list is the most-visited page in the dashboard: a search
 * query per page view, for staff who never open this, is a bill paid forever.
 */
const PrepareOrderDialogContent = ({ onClose }: { onClose: () => void }) => {
  const intl = useIntl();
  const [customer, setCustomer] = useState<PrepareCheckoutCustomer | null>(null);
  const { loadMore, search, result } = useCustomerSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA,
  });
  const { prepare, loading, error, disabledReason, currencyCode, preparedCheckoutId } =
    usePrepareCheckout(customer);
  const customers = mapEdgesToItems(result?.data?.search) ?? [];
  const choices = customers.map(({ id, email, firstName, lastName }) => {
    const name = getFullName({ firstName, lastName });

    return { value: id, label: name ? `${name} (${email})` : email };
  });
  const handleSelect = (event: { target: { value: string } }) => {
    const picked = customers.find(item => item.id === event.target.value);

    setCustomer(picked ? { id: picked.id, email: picked.email } : null);
  };

  return (
    <DashboardModal.Content size="xs" data-test-id="prepare-order-dialog">
      <DashboardModal.Header>{intl.formatMessage(messages.addOrder)}</DashboardModal.Header>
      <Box display="flex" flexDirection="column" gap={3}>
        <Text size={2}>{intl.formatMessage(messages.dialogSubtitle)}</Text>
        <Combobox
          data-test-id="prepare-order-customer-field"
          label={intl.formatMessage(messages.customerLabel)}
          options={choices}
          fetchOptions={search}
          fetchMore={{
            onFetchMore: loadMore,
            hasMore: !!result?.data?.search?.pageInfo?.hasNextPage,
            loading: !!result?.loading,
          }}
          name="customer"
          value={
            customer
              ? {
                  label: choices.find(choice => choice.value === customer.id)?.label ?? "",
                  value: customer.id,
                }
              : null
          }
          onChange={handleSelect}
        />
        {disabledReason && (disabledReason !== "no-email" || !!customer) && (
          <Text size={2} color="default2" data-test-id="prepare-order-disabled-reason">
            {intl.formatMessage(
              disabledReason === "no-email"
                ? messages.noEmail
                : disabledReason === "no-channel"
                  ? messages.noChannel
                  : messages.noStorefront,
            )}
          </Text>
        )}
        {error && (
          <Text size={2} color="critical1" data-test-id="prepare-order-error">
            {error}
          </Text>
        )}
        {preparedCheckoutId && (
          <QuotedFreightField
            key={preparedCheckoutId}
            checkoutId={preparedCheckoutId}
            currencyCode={currencyCode}
          />
        )}
      </Box>
      <DashboardModal.Actions>
        <Button variant="secondary" data-test-id="prepare-order-cancel" onClick={onClose}>
          {intl.formatMessage(buttonMessages.cancel)}
        </Button>
        <Button
          data-test-id="prepare-order-confirm"
          disabled={!customer || !!disabledReason || loading}
          onClick={prepare}
        >
          {intl.formatMessage(loading ? messages.creating : messages.confirm)}
        </Button>
      </DashboardModal.Actions>
    </DashboardModal.Content>
  );
};

/** "Add order" on the order list: pick a customer, then run the same prepare flow. */
export const PrepareOrderButton = () => {
  const intl = useIntl();
  const userPermissions = useUserPermissions();
  const [open, setOpen] = useState(false);

  if (!hasPermissions(userPermissions ?? [], [PermissionEnum.IMPERSONATE_USER])) {
    return null;
  }

  return (
    <>
      <Button variant="secondary" data-test-id="add-order-button" onClick={() => setOpen(true)}>
        {intl.formatMessage(messages.addOrder)}
      </Button>
      <DashboardModal open={open} onChange={setOpen}>
        {open && <PrepareOrderDialogContent onClose={() => setOpen(false)} />}
      </DashboardModal>
    </>
  );
};

PrepareOrderButton.displayName = "PrepareOrderButton";
